import { useEffect, useRef, useState } from "react";
import { Box, CircularProgress, IconButton, Skeleton, Typography } from "@mui/material";
import { useQueries, useQueryClient } from "@tanstack/react-query";
import { ArrowRight, Check, Circle, Loader2, Plus, Sparkles, Trash2, WandSparkles } from "lucide-react";

import apiClient from "../../../api-client";
import { Button } from "../../../components/Button";
import { useAuth } from "../../../components/contexts/AuthContext";
import { brand } from "../../../theme/theme";
import { BaseTextPanel } from "../workflow/BaseTextPanel";
import { ADAPTATION_PLATFORMS } from "../workflow/platformMeta";
import { PieceTile } from "./PieceTile";
import { panelSx, pieceState } from "./campaignUi";

export const CAMPAIGN_PIECE_QUERY_KEY = "campaign-piece";

function PhaseItem({ state, label }) {
  const color = state === "done" ? "#0F9D58" : state === "active" ? brand.pinkDark : "#9CA3AF";
  return (
    <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.625, color, fontSize: "0.8125rem", fontWeight: state === "active" ? 700 : 500 }}>
      {state === "done" ? (
        <Check size={14} strokeWidth={3} />
      ) : state === "active" ? (
        <Loader2 size={14} style={{ animation: "spin 1.2s linear infinite" }} />
      ) : (
        <Circle size={13} />
      )}
      {label}
    </Box>
  );
}

/** Base text of a single piece, editable in place until it is adapted to the networks. */
function SingleBaseText({ piece, disabled, notify, onSaved, onAdapt, isAdapting, initialChannels = null, isMock = false }) {
  const saved = piece.raw_content || "";
  const [text, setText] = useState(saved);
  const [isSaving, setIsSaving] = useState(false);
  // No network preselected (except in the tutorial): the consultant picks them here.
  const [channels, setChannels] = useState(() => initialChannels || []);

  const toggleChannel = (platform) =>
    setChannels((prev) => (prev.includes(platform) ? prev.filter((item) => item !== platform) : [...prev, platform]));

  useEffect(() => setText(saved), [saved]);

  const handleSave = async () => {
    if (isMock) return;
    setIsSaving(true);
    try {
      await apiClient.patch(`content-generation/pieces/${piece.id}/`, { raw_content: text });
      onSaved?.();
      notify?.("Texto base guardado");
    } catch (error) {
      notify?.(error?.response?.data?.detail || "No se pudo guardar el texto base.", true);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <BaseTextPanel
      value={text}
      onChange={setText}
      readOnly={disabled}
      isDirty={text !== saved}
      onSave={handleSave}
      onDiscard={() => setText(saved)}
      isSaving={isSaving}
      channels={ADAPTATION_PLATFORMS}
      selectedChannels={channels}
      onToggleChannel={toggleChannel}
      onGenerateAdaptations={() => onAdapt(text, channels)}
      isGeneratingAdaptations={isAdapting}
      sx={{ border: "none", borderRadius: 0 }}
    />
  );
}

// Next steps of every piece once the batch's base texts are ready.
const MANAGE_STEPS = ["Abre un contenido", "Genera sus adaptaciones a redes", "Envíalo al cliente"];

/**
 * Right column of the batch page: progress of the batch, the proposed topics to
 * review, and the generated pieces (adapt them to the networks, send them to the client).
 */
export function CampaignProgressPanel({
  phase, // "briefing" | "topics_loading" | "topics_review" | "generating" | "done"
  plannedPieces,
  topics,
  onTopicChange,
  onRemoveTopic,
  onAddTopic,
  isConfirming,
  onConfirmTopics,
  progress,
  pieceIds,
  onOpenPiece,
  onManagePieces,
  onSingleReady, // (piece) => void — a single piece leaves this page once it is adapted
  onSingleStageChange, // ("base" | "adapting" | null) => void — single piece stage, for the tutorial
  notify,
  single = false, // one piece created from a topic: no topic proposal/review steps
  mockPieces = null, // tutorial: piece details shown instead of fetching `pieceIds`
  mockSelectedChannels = null, // tutorial: networks shown already ticked below the single base text
}) {
  const { interceptorReady } = useAuth();
  const queryClient = useQueryClient();
  const singleReadyRef = useRef(false);
  const [adaptingIds, setAdaptingIds] = useState(() => new Set());
  const [isAdapting, setIsAdapting] = useState(false);

  const month = new Date().toLocaleDateString("es-ES", { month: "long" });
  const topicCount = topics.filter((topic) => String(topic || "").trim()).length;
  const total =
    phase === "topics_review" || phase === "topics_loading"
      ? topicCount || progress.total
      : progress.total || pieceIds.length;

  const pieceQueries = useQueries({
    queries: pieceIds.map((id) => ({
      queryKey: [CAMPAIGN_PIECE_QUERY_KEY, id],
      queryFn: () => apiClient.get(`content-generation/pieces/${id}/`).then((res) => res.data),
      enabled: interceptorReady && !mockPieces,
      refetchInterval: (query) => {
        const state = pieceState(query.state.data, adaptingIds);
        return state === "ready" || state === "error" || state === "base" ? false : 4000;
      },
    })),
  });
  const pieces = pieceIds.map((id, index) =>
    mockPieces ? mockPieces[index] || null : pieceQueries[index]?.data || null
  );
  const states = pieces.map((piece) => pieceState(piece, adaptingIds));
  const counts = states.reduce((acc, state) => ({ ...acc, [state]: (acc[state] || 0) + 1 }), {});
  const readyCount = counts.ready || 0;
  const baseCount = counts.base || 0;
  const adaptingCount = counts.adapting || 0;
  const errorCount = counts.error || 0;
  const composingCount = pieceIds.length
    ? (counts.composing || 0) + (counts.queued || 0)
    : Math.max(0, (progress.total || 0) - (progress.completed || 0) - (progress.failed || 0));
  const allTextsDone = phase === "done" || (pieceIds.length > 0 && composingCount === 0);
  // A single piece shows its base text (editable) here until it is adapted to the networks.
  const showSingleBase = single && Boolean(pieces[0]) && states[0] === "base";

  const singleStage = !single ? null : isAdapting || adaptingCount ? "adapting" : showSingleBase ? "base" : null;
  useEffect(() => {
    onSingleStageChange?.(singleStage);
  }, [onSingleStageChange, singleStage]);

  // Once adapted, the single piece continues on its own page (workflow stepper).
  useEffect(() => {
    if (!single || singleReadyRef.current || !pieces[0]) return;
    if (states[0] === "ready") {
      singleReadyRef.current = true;
      onSingleReady?.(pieces[0]);
    }
  }, [single, pieces, states, onSingleReady]);

  // ── Progress header ──
  const processed = pieceIds.length
    ? readyCount + baseCount + adaptingCount + errorCount
    : (progress.completed || 0) + (progress.failed || 0);
  let percent = 0;
  if (phase === "topics_review") percent = 15;
  else if (phase === "generating" || phase === "done") {
    const textShare = total ? processed / total : 0;
    const adaptShare = total ? readyCount / total : 0;
    // A batch ends at the base texts; a single piece is also adapted here.
    percent = single ? Math.round(20 + textShare * 50 + adaptShare * 30) : Math.round(20 + textShare * 80);
  } else if (phase === "topics_loading") percent = 5;
  percent = Math.min(100, percent);

  const notStarted = phase === "briefing";
  const phases = [
    { key: "ideas", label: "Ideas de contenido", state: notStarted ? "pending" : phase === "topics_loading" ? "active" : "done" },
    {
      key: "review",
      label: "Revisión de temas",
      state: notStarted || phase === "topics_loading" ? "pending" : phase === "topics_review" ? "active" : "done",
    },
    { key: "texts", label: "Textos base", state: phase === "generating" && !allTextsDone ? "active" : allTextsDone ? "done" : "pending" },
  ];
  const singlePhases = [
    {
      key: "base",
      label: "Contenido base (prompt final)",
      state: notStarted || errorCount ? "pending" : allTextsDone ? "done" : "active",
    },
    {
      key: "adapt",
      label: "Adaptaciones a redes",
      state: readyCount ? "done" : adaptingCount ? "active" : "pending",
    },
  ];

  let headerTitle = `Nueva tanda de ${month}`;
  let headerSubtitle = `Completa el briefing y genera ${plannedPieces} ${plannedPieces === 1 ? "pieza" : "piezas"}.`;
  if (phase === "topics_loading") {
    headerTitle = `Preparando la tanda de ${month}`;
    headerSubtitle = "La IA está proponiendo los temas de las piezas.";
  } else if (phase === "topics_review") {
    headerTitle = "Revisa los temas de la tanda";
    headerSubtitle = "Ajusta, añade o quita temas. Al generar se redacta el texto base de cada pieza.";
  } else if (phase === "generating" || phase === "done") {
    if (!allTextsDone) {
      headerTitle = `Generando la tanda de ${month}`;
      headerSubtitle = `Redactando textos base · pieza ${Math.min(processed + 1, total)} de ${total}`;
    } else {
      headerTitle = "Contenido base generado y guardado";
      headerSubtitle = "Ahora gestiona cada contenido por separado desde la tabla de contenidos.";
    }
  }

  if (single) {
    headerTitle = "Nuevo contenido";
    headerSubtitle = "Completa el briefing y genera la pieza.";
    if (phase === "generating" || phase === "done") {
      if (!allTextsDone) {
        headerTitle = "Generando el contenido";
        headerSubtitle = "Redactando el texto base a partir de la temática.";
      } else if (errorCount) {
        headerTitle = "No se ha podido generar el contenido";
        headerSubtitle = "Ábrelo para ver el error o vuelve a intentarlo.";
      } else if (readyCount) {
        headerTitle = "Adaptaciones generadas";
        headerSubtitle = "Abriendo el contenido...";
      } else if (adaptingCount) {
        headerTitle = "Adaptando el contenido a cada red";
        headerSubtitle = "En unos segundos tendrás una versión por canal.";
      } else {
        headerTitle = "Contenido base listo";
        headerSubtitle = "Revísalo, elige las redes y genera sus adaptaciones.";
      }
    }
  }

  // Single piece: save the edited base text and adapt it to the chosen networks.
  const handleAdaptSingle = async (text, platforms) => {
    const piece = pieces[0];
    if (!piece || !platforms.length || mockPieces) return;
    setIsAdapting(true);
    try {
      if (text !== (piece.raw_content || "")) {
        await apiClient.patch(`content-generation/pieces/${piece.id}/`, { raw_content: text });
      }
      await apiClient.post(`content-generation/pieces/${piece.id}/generate_adaptations/`, { platforms });
      setAdaptingIds((prev) => new Set(prev).add(piece.id));
      queryClient.invalidateQueries({ queryKey: [CAMPAIGN_PIECE_QUERY_KEY, piece.id] });
    } catch (error) {
      notify?.(error?.response?.data?.error || error?.response?.data?.detail || "No se ha podido adaptar el contenido.", true);
    } finally {
      setIsAdapting(false);
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
      <Box data-tutorial={phase === "topics_loading" ? "cg-topics-polling" : "cg-campaign-progress"} sx={{ ...panelSx, p: 2.5 }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
          <Box sx={{ width: 36, height: 36, minWidth: 36, borderRadius: "10px", background: brand.gradientDiagonal, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Sparkles size={18} color="#fff" />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontWeight: 800, fontSize: "1rem", color: "#0F172A", "&::first-letter": { textTransform: "uppercase" } }}>
              {headerTitle}
            </Typography>
            <Typography sx={{ fontSize: "0.8125rem", color: "#6B7280" }}>{headerSubtitle}</Typography>
          </Box>
          {!notStarted && (
            <Typography sx={{ fontWeight: 800, fontSize: "1.375rem", background: brand.gradient, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
              {percent}%
            </Typography>
          )}
        </Box>
        <Box sx={{ mt: 1.75, height: 6, borderRadius: 6, backgroundColor: "#F1F5F9", overflow: "hidden" }}>
          <Box sx={{ width: `${percent}%`, height: "100%", borderRadius: 6, background: brand.gradient, transition: "width 0.6s ease" }} />
        </Box>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: { xs: 1.25, md: 2.5 }, mt: 1.5 }}>
          {(single ? singlePhases : phases).map((item) => (
            <PhaseItem key={item.key} state={item.state} label={item.label} />
          ))}
        </Box>
        {allTextsDone && !single && (
          <Box
            data-tutorial="cg-manage-pieces"
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1.75,
              mt: 2,
              p: 2.25,
              borderRadius: "14px",
              border: `1px solid ${brand.softBorder}`,
              background: `linear-gradient(135deg, ${brand.soft} 0%, #FFF8EF 100%)`,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.25 }}>
              <Box sx={{ width: 32, height: 32, minWidth: 32, borderRadius: "10px", backgroundColor: "#0F9D58", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Check size={17} color="#fff" strokeWidth={3} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontWeight: 800, fontSize: "0.9375rem", color: "#0F172A" }}>
                  Contenido base generado y guardado: ya puedes generar adaptaciones de todos ellos.
                </Typography>
                <Typography sx={{ fontSize: "0.8125rem", color: "#6B7280", mt: 0.25 }}>
                  Cada contenido sigue su propio camino. Ábrelo desde la tabla y complétalo uno a uno:
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1 }}>
              {MANAGE_STEPS.map((step, index) => (
                <Box key={step} sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}>
                  {index > 0 && <ArrowRight size={14} color="#9CA3AF" />}
                  <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.75, px: 1.25, py: 0.625, borderRadius: "999px", backgroundColor: "#fff", border: "1px solid #E5E7EB", fontSize: "0.8125rem", fontWeight: 600, color: "#374151" }}>
                    <Box component="span" sx={{ width: 18, height: 18, borderRadius: "50%", backgroundColor: brand.soft, color: brand.pinkDark, fontSize: "0.6875rem", fontWeight: 800, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
                      {index + 1}
                    </Box>
                    {step}
                  </Box>
                </Box>
              ))}
            </Box>
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <Button
                onClick={onManagePieces}
                sx={{ borderRadius: "999px", background: brand.gradient, "&:hover": { background: brand.gradientHover } }}
              >
                Gestionar cada contenido base <ArrowRight size={15} />
              </Button>
            </Box>
          </Box>
        )}
      </Box>

      {notStarted && (
        <Box sx={{ ...panelSx, p: 3, display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", gap: 1 }}>
          <Box sx={{ width: 44, height: 44, borderRadius: "12px", backgroundColor: brand.soft, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <WandSparkles size={20} color={brand.pinkDark} />
          </Box>
          <Typography sx={{ fontWeight: 800, fontSize: "1rem", color: "#0F172A" }}>
            {single ? "Aquí aparecerá tu contenido" : "Aquí aparecerán las piezas de la tanda"}
          </Typography>
          <Typography sx={{ fontSize: "0.875rem", color: "#6B7280", maxWidth: 460 }}>
            {single
              ? "La IA redacta el texto base a partir de la temática y después puedes adaptarlo a las redes antes de enviarlo al cliente."
              : "Primero la IA propone un tema por pieza para que los revises. Después redacta el texto base de cada una y puedes adaptarlas a las redes antes de enviarlas al cliente."}
          </Typography>
        </Box>
      )}

      {phase === "topics_loading" && (
        <Box sx={{ ...panelSx, p: 2.5, display: "flex", flexDirection: "column", gap: 1.25 }}>
          <Typography sx={{ fontWeight: 800, fontSize: "1rem", color: "#0F172A", mb: 0.5 }}>Temas propuestos</Typography>
          {Array.from({ length: Math.min(progress.total || 6, 8) }).map((_, index) => (
            <Skeleton key={index} variant="rounded" height={44} sx={{ borderRadius: "10px" }} />
          ))}
          <Typography sx={{ fontSize: "0.8125rem", color: "#6B7280", textAlign: "center", mt: 1 }}>
            Esto puede tardar unos segundos. No abandones la página.
          </Typography>
        </Box>
      )}

      {phase === "topics_review" && (
        <Box data-tutorial="cg-topics-review" sx={{ ...panelSx, p: 2.5, display: "flex", flexDirection: "column", gap: 1.5 }}>
          <Box data-tutorial="cg-topics-list" sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 1 }}>
            <Typography sx={{ fontWeight: 800, fontSize: "1rem", color: "#0F172A" }}>Temas propuestos</Typography>
            <Typography sx={{ fontSize: "0.8125rem", color: "#6B7280" }}>
              {topicCount} {topicCount === 1 ? "tema" : "temas"}
            </Typography>
          </Box>
          <Box data-tutorial="cg-topics-box" sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 1 }}>
            {topics.map((topic, index) => (
              <Box
                key={`topic-${index}`}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  pl: 1.25,
                  pr: 0.5,
                  borderRadius: "12px",
                  border: "1px solid #E5E7EB",
                  backgroundColor: "#fff",
                  "&:focus-within": { borderColor: brand.pink, boxShadow: `0 0 0 3px ${brand.soft}` },
                }}
              >
                <Typography sx={{ fontSize: "0.75rem", fontWeight: 700, color: brand.pink, minWidth: 18 }}>{index + 1}</Typography>
                <Box
                  component="input"
                  value={topic}
                  onChange={(event) => onTopicChange(index, event.target.value)}
                  placeholder={`Tema ${index + 1}`}
                  aria-label={`Tema ${index + 1}`}
                  disabled={isConfirming}
                  sx={{ flex: 1, minWidth: 0, border: "none", outline: "none", py: 1.25, fontSize: "0.875rem", fontFamily: "inherit", backgroundColor: "transparent", color: "#111827" }}
                />
                <IconButton aria-label={`Quitar tema ${index + 1}`} size="small" onClick={() => onRemoveTopic(index)} disabled={isConfirming || topics.length <= 1}>
                  <Trash2 size={15} />
                </IconButton>
              </Box>
            ))}
            <Box
              component="button"
              type="button"
              onClick={onAddTopic}
              disabled={isConfirming || topics.length >= 50}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 0.75,
                py: 1.25,
                borderRadius: "12px",
                border: "1px dashed #CBD5E1",
                backgroundColor: "transparent",
                color: "#6B7280",
                font: "inherit",
                fontSize: "0.875rem",
                fontWeight: 600,
                cursor: "pointer",
                "&:hover": { borderColor: brand.pink, color: brand.pinkDark },
              }}
            >
              <Plus size={15} /> Añadir tema
            </Box>
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: { xs: "stretch", md: "center" },
              justifyContent: "space-between",
              flexDirection: { xs: "column", md: "row" },
              gap: 1.5,
              mt: 1,
              pt: 2,
              borderTop: "1px solid #F1F5F9",
            }}
          >
            <Typography sx={{ fontSize: "0.875rem", color: "#6B7280" }}>
              Al aceptar se redacta el <strong style={{ color: "#111827" }}>texto base</strong> de cada tema.
            </Typography>
            <Button
              onClick={onConfirmTopics}
              disabled={isConfirming || !topicCount}
              variant="dark"
            >
              {isConfirming ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : <Check size={15} />}
              {isConfirming ? "Lanzando..." : `Aceptar ${topicCount} ${topicCount === 1 ? "tema" : "temas"}`}
              {!isConfirming && <ArrowRight size={15} />}
            </Button>
          </Box>
        </Box>
      )}

      {(phase === "generating" || phase === "done") && (
        <Box sx={{ ...panelSx, display: "flex", flexDirection: "column", overflow: "hidden" }}>
          <Box sx={{ display: showSingleBase ? "none" : "flex", alignItems: { xs: "flex-start", sm: "center" }, justifyContent: "space-between", flexDirection: { xs: "column", sm: "row" }, gap: 1, px: 2.5, py: 2 }}>
            <Box sx={{ display: "flex", alignItems: "baseline", gap: 1.25, flexWrap: "wrap" }}>
              <Typography sx={{ fontWeight: 800, fontSize: "1rem", color: "#0F172A" }}>
                {single ? "Contenido generado" : "Piezas generadas"}
              </Typography>
              {!single && (
                <Typography sx={{ fontSize: "0.8125rem", color: "#6B7280" }}>
                  {readyCount + baseCount} de {total} {total === 1 ? "generado" : "generados"}
                  {errorCount ? ` · ${errorCount} con error` : ""}
                </Typography>
              )}
            </Box>
          </Box>

          {showSingleBase ? (
            <SingleBaseText
              piece={pieces[0]}
              disabled={isAdapting}
              isAdapting={isAdapting}
              onAdapt={handleAdaptSingle}
              initialChannels={mockSelectedChannels}
              isMock={Boolean(mockPieces)}
              notify={notify}
              onSaved={() => queryClient.invalidateQueries({ queryKey: [CAMPAIGN_PIECE_QUERY_KEY, pieces[0].id] })}
            />
          ) : (
          <Box
            data-tutorial="cg-pieces-grid"
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", xl: "repeat(4, 1fr)" },
              gap: 1.5,
              px: 2.5,
              pb: 2.5,
              "@media (min-width:1280px) and (max-width:1535px)": { gridTemplateColumns: "repeat(3, 1fr)" },
            }}
          >
            {pieceIds.length === 0
              ? Array.from({ length: Math.max(progress.total || 0, 1) }).map((_, index) => (
                <PieceTile key={`queued-${index}`} piece={null} state={index < (progress.completed || 0) ? "composing" : "queued"} index={index} onOpen={() => {}} />
              ))
              : pieceIds.map((id, index) => (
                // Batch pieces are managed one by one from the table, not from here.
                <PieceTile key={id} piece={pieces[index]} state={states[index]} index={index} onOpen={single ? onOpenPiece : undefined} />
              ))}
          </Box>
          )}
        </Box>
      )}
    </Box>
  );
}
